import { useEffect, useMemo, useState } from 'react';
import { createBybitUrl, replacePath, replaceVar } from '@web3/utils';
import { useLocale } from '../../store/i18nMessage';
import { checkSupport, subAppPaths } from '../../utils/constants';
import { ANNOUNCEMENT_LINK, BLOG_LINK, DISCORD_LINK, PRIVACY_POLICY_LINK, TELEGRAM_LINK, TWITTER_LINK, USER_AGREEMENT_LINK } from '../../utils/url';
import qrCodeImg from './images/download-app.png';
import { mediaIcons } from './mediaIcons';
import { useScreen } from '../../store/screen';
import { isSupportCountry } from '../../utils/regulationCountry';
import './style.scss';

export type Web3FooterProps = {
  onlyCopyRight?: boolean;
};

type SocialMedia = {
  url: string;
  icon: React.ReactNode;
  buttonName: string;
};

const mediaList: SocialMedia[] = [
  {
    url: DISCORD_LINK,
    icon: mediaIcons.discord,
    buttonName: 'Discord',
  },
  {
    url: TELEGRAM_LINK,
    icon: mediaIcons.telegram,
    buttonName: 'Telegram',
  },
  {
    url: TWITTER_LINK,
    icon: mediaIcons.twitter,
    buttonName: 'Twitter',
  },
];

export function Web3Footer({ onlyCopyRight = false }: Web3FooterProps) {
  const { t, locale } = useLocale();
  const screen = useScreen();
  const allFooterProductLinks = useMemo(
    () => [
      { key: 'staking', text: t('staking'), href: `/${locale}/web3/staking`, onClick: 'Staking' },
      { key: 'swap', text: t('swap'), href: `/${locale}/web3/swap-bridge`, onClick: 'Swap' },
      { key: 'airdrop', text: t('airdropArcade'), href: `/${locale}/web3/airdrop`, onClick: 'Airdrop' },
    ],
    [t, locale],
  );
  const [footerProductLinks, setFooterProductLinks] = useState(allFooterProductLinks);
  const makeUrl = (url: string) => createBybitUrl(replaceVar(url, { locale }));
  const footerLinks = [
    [makeUrl(ANNOUNCEMENT_LINK), 'announcement', t('announcement')],
    [makeUrl(BLOG_LINK), 'blog', t('blog')],
    [replacePath(subAppPaths['crypto-ark']), 'TheCryptoArk', 'TheCryptoArk'],
  ];

  useEffect(() => {
    // 获取当前用户合规信息是否NZL
    isSupportCountry('NZL').then((isNZL) => {
      if (isNZL) {
        const newList = JSON.parse(JSON.stringify(allFooterProductLinks));
        newList.splice(2, 1);
        setFooterProductLinks(newList);
      } else {
        setFooterProductLinks(allFooterProductLinks);
      }
    });
  }, [allFooterProductLinks]);

  if (screen.lg) {
    return null;
  }

  return (
    <footer id="web3-footer" className="web3-footer-bar">
      {!onlyCopyRight && (
        <div className="footer-top">
          <div>
            <div className="flex">
              <div className="svg-full h-9">{brandLogo}</div>
            </div>
            <div className="mt-10 flex gap-9">
              {mediaList.map((media) => {
                return (
                  <a
                    href={media.url}
                    key={media.buttonName}
                    target="_blank"
                    className="media-link hover:!text-bds-brand-700-normal"
                    rel="nofollow noopener noreferrer"
                    data-spm-even="SpotWeb3FooterClick"
                    data-button_name={media.buttonName}
                  >
                    <div className="w-6 h-6 svg-full">{media.icon}</div>
                  </a>
                );
              })}
            </div>
          </div>
          <div />
          <div>
            <h3 className="text-xl font-semibold text-bds-brand-700-normal">{t('about')}</h3>
            {footerLinks.map(([url, buttonName, text]) => {
              return (
                <a
                  href={url}
                  target="_blank"
                  key={buttonName}
                  rel="nofollow noopener noreferrer"
                  className="footer-link"
                  data-spm-even="SpotWeb3FooterClick"
                  data-button_name={buttonName}
                >
                  {text}
                </a>
              );
            })}
          </div>
          <div className="flex-shrink-0">
            <h3 className="text-xl font-semibold text-bds-brand-700-normal">{t('product')}</h3>
            {footerProductLinks?.map((link) => {
              return (
                checkSupport(locale, link.key) && (
                  <a
                    key={link.key}
                    href={link.href}
                    rel="noreferrer"
                    target="_blank"
                    className="footer-link"
                    data-spm-even="SpotWeb3FooterClick"
                    data-button_name={link.onClick}
                  >
                    {link.text}
                  </a>
                )
              );
            })}
          </div>
          <div>
            <img src={qrCodeImg} alt="qrCode" className="w-[128px]" />
            <p className="mt-4 text-xs text-white">{t('scan_to_download')}</p>
          </div>
        </div>
      )}

      <div className="footer-bottom">
        <span className=" text-bds-gray-t3">Copyright © {new Date().getFullYear()} Bybit. </span>
        <div className="ltr:ml-6 rtl:mr-6 flex items-center">
          <a
            href={makeUrl(USER_AGREEMENT_LINK)}
            target="_blank"
            rel="nofollow noopener noreferrer"
            data-spm-even="SpotWeb3FooterClick"
            data-button_name="UserAgreement"
          >
            {t('user_agreement')}
          </a>
          <div className="split-line" />
          <a
            href={makeUrl(PRIVACY_POLICY_LINK)}
            target="_blank"
            rel="nofollow noopener noreferrer"
            data-spm-even="SpotWeb3FooterClick"
            data-button_name="PrivacyPolicy"
          >
            {t('privacy_policy')}
          </a>
        </div>
      </div>
    </footer>
  );
}
const brandLogo = (
  <svg width="696" height="134" viewBox="0 0 696 134" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M243.675 96.4708V0H262.509V96.4708H243.675Z" fill="#FF9C2E" />
    <path
      d="M0 120.591V24.1202H38.7505C57.5852 24.1202 68.5617 34.6862 68.5617 51.2185C68.5617 61.9247 61.5143 68.8319 56.6392 71.1343C62.4653 73.8422 69.9181 79.9436 69.9181 92.812C69.9181 110.831 57.5902 120.591 40.3822 120.591H0ZM37.2589 40.9178H18.8297V63.141H37.2589C45.2523 63.141 49.727 58.6713 49.727 52.0294C49.727 45.3874 45.2573 40.9178 37.2589 40.9178ZM38.4802 80.0787H18.8347V103.788H38.4802C47.0141 103.788 51.0834 98.3678 51.0834 91.866C51.0834 85.3642 47.0191 80.0787 38.4802 80.0787Z"
      fill="white"
    />
    <path
      d="M120.351 81.0247V120.591H101.651V81.0247L72.6559 24.1152H93.1173L111.136 63.0008L128.885 24.1152H149.346L120.351 81.0247Z"
      fill="white"
    />
    <path
      d="M159.236 120.591V24.1202H197.987C216.822 24.1202 227.798 34.6862 227.798 51.2185C227.798 61.9247 220.751 68.8319 215.876 71.1343C221.702 73.8422 229.155 79.9436 229.155 92.812C229.155 110.831 216.827 120.591 199.619 120.591H159.236ZM196.495 40.9178H178.066V63.141H196.495C204.489 63.141 208.963 58.6713 208.963 52.0294C208.963 45.3874 204.494 40.9178 196.495 40.9178ZM197.717 80.0787H178.071V103.788H197.717C206.251 103.788 210.32 98.3678 210.32 91.866C210.32 85.3642 206.256 80.0787 197.717 80.0787Z"
      fill="white"
    />
    <path d="M323.103 40.9228V120.596H304.268V40.9228H279.067V24.1202H348.304V40.9228H323.103Z" fill="white" />
    <rect width="316.502" height="133.658" transform="translate(378.681)" fill="#9747FF" />
    <path
      d="M490.284 33.2113L470.444 110.828H462.704L445.48 48.6911L428.256 110.828H420.516L400.676 33.2113H409.506L424.659 95.7844L441.773 33.2113H449.186L466.301 95.7844L481.454 33.2113H490.284ZM547.027 110.828H498.843V33.2113H547.027V40.6242H507.128V67.9863H541.14V75.3991H507.128V103.415H547.027V110.828ZM613.155 89.3527C613.155 103.415 603.779 110.828 590.044 110.828H559.848V33.2113H589.063C602.58 33.2113 611.955 40.9512 611.955 53.9237C611.955 62.0996 607.05 68.5313 600.509 70.9296C607.922 73.6549 613.155 80.0866 613.155 89.3527ZM604.87 89.1347C604.87 80.4137 598.874 74.9631 589.281 74.9631H568.132V103.415H589.281C598.874 103.415 604.87 97.8557 604.87 89.1347ZM603.67 54.0327C603.67 44.7666 597.021 40.6242 588.409 40.6242H568.132V67.5502H588.409C597.021 67.5502 603.67 63.2987 603.67 54.0327ZM667.221 89.6797C667.221 103.96 657.083 111.591 644.438 111.591C632.228 111.591 622.308 105.159 621.654 91.0969H629.503C630.157 100.799 637.025 104.614 644.438 104.614C652.832 104.614 659.372 99.0548 659.372 89.5707C659.372 80.1957 654.685 74.527 644.111 74.527H642.475V67.6592H644.111C653.595 67.6592 657.955 62.3176 657.955 53.5966C657.955 44.3306 651.96 39.425 644.438 39.425C636.262 39.425 631.247 44.4396 630.484 52.3975H622.635C623.507 39.8611 632.664 32.4482 644.438 32.4482C657.083 32.4482 665.804 40.8422 665.804 53.4876C665.804 61.6636 662.316 67.5502 655.339 70.7116C663.188 73.6549 667.221 79.9776 667.221 89.6797Z"
      fill="white"
    />
  </svg>
);
